import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaArrowLeft, FaSave, FaCheck, FaStickyNote } from 'react-icons/fa';
import axios from 'axios';

const RECOGNIZED_KEYS = new Set([
  'weight',
  'ladder_number',
  'ladder_type',
  'ladder_material',
  'manufacturer',
  'ladder_part_number',
  'acquisition_date',
  'signed_by'
]);

const extractValueFromDescription = (description = '', label = '') => {
  if (!description || !label) return null;
  const parts = description
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);

  const match = parts.find((part) => {
    const [lhs] = part.split(':');
    return lhs && lhs.trim().toLowerCase() === label.trim().toLowerCase();
  });

  if (!match) return null;
  const [, ...rest] = match.split(':');
  return rest.join(':').trim();
};

const ServiceDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [mekanikere, setMekanikere] = useState([]);
  const [kilometerstand, setKilometerstand] = useState('');
  const [mechanicSignature, setMechanicSignature] = useState('');
  const [subcategoryResults, setSubcategoryResults] = useState({});
  const [subcategoryFields, setSubcategoryFields] = useState({}); // subcatId -> fieldDefs
  const [subcategoryRepeatable, setSubcategoryRepeatable] = useState({}); // subcatId -> boolean
  const [activeSubcategories, setActiveSubcategories] = useState({}); // subcatId -> boolean
  const [notesActive, setNotesActive] = useState({}); // subcatId -> boolean visual toggle
  const serverDraftApplied = useRef(false);

  const bilId = searchParams.get('bil_id');
  const interntNummer = searchParams.get('internt_nummer');
  const categoryIds = useMemo(() => {
    const raw = searchParams.get('categories');
    if (!raw) return [];
    return raw
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter((n) => !Number.isNaN(n));
  }, [searchParams]);

  // Session persistence (1 uge)
  const sessionKey = useMemo(() => `serviceSession:${bilId}:${categoryIds.join(',')}`, [bilId, categoryIds]);
  const categoryNames = useMemo(() => {
    const names = new Set();
    categories.forEach((item) => {
      if (item?.category_name) {
        names.add(item.category_name);
      }
    });
    return Array.from(names);
  }, [categories]);
  const restoredFromSession = useRef(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Hent kategorier med underkategorier
      const categoriesResponse = await axios.get(`/api/services/categories/${categoryIds.join(',')}`);
      setCategories(categoriesResponse.data);

      // Hent mekanikere
      const mekanikereResponse = await axios.get('/api/services/mekanikere');
      setMekanikere(mekanikereResponse.data);

      // Hent kilometerstand
      const kilometerResponse = await axios.get(`/api/services/bil/${bilId}/kilometerstand`);
      setKilometerstand(kilometerResponse.data.mot_kilometerstand || '');
      // Hent felter for hver underkategori
      const subcategoryIds = categoriesResponse.data
        .filter(item => item.subcategory_id)
        .map(item => item.subcategory_id);

      const uniqueSubcategoryIds = Array.from(new Set(subcategoryIds));
      const fieldPromises = uniqueSubcategoryIds.map(id => axios.get(`/api/indstillinger/eftersynsskema/subcategories/${id}/fields`).then(r => ({ id, fields: r.data })));
      const fieldsArray = await Promise.all(fieldPromises);

      const fieldsMap = {};
      const repeatableMap = {};
      fieldsArray.forEach(({ id, fields }) => {
        // Sorter felter efter sort_order for korrekt rækkefølge
        const sortedFields = (fields || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        fieldsMap[id] = sortedFields;
        repeatableMap[id] = sortedFields.some(f => Boolean(f.repeatable));
      });
      setSubcategoryFields(fieldsMap);
      setSubcategoryRepeatable(repeatableMap);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  // Gendan session efter data er hentet (kun én gang)
  useEffect(() => {
    if (loading || restoredFromSession.current) return;
    try {
      const raw = localStorage.getItem(sessionKey);
      if (raw) {
        const payload = JSON.parse(raw);
        if (payload && typeof payload.ts === 'number') {
          const maxAge = 7 * 24 * 60 * 60 * 1000; // 1 uge
          if (Date.now() - payload.ts <= maxAge) {
            if (typeof payload.kilometerstand !== 'undefined') setKilometerstand(payload.kilometerstand);
            if (typeof payload.mechanicSignature !== 'undefined') setMechanicSignature(payload.mechanicSignature);
            if (payload.activeSubcategories && typeof payload.activeSubcategories === 'object') setActiveSubcategories(payload.activeSubcategories);
            if (payload.subcategoryResults && typeof payload.subcategoryResults === 'object') setSubcategoryResults(payload.subcategoryResults);
            if (payload.notesActive && typeof payload.notesActive === 'object') setNotesActive(payload.notesActive);
          } else {
            // Forældet kladde
            localStorage.removeItem(sessionKey);
          }
        }
      }
    } catch (_) {}
    restoredFromSession.current = true;
  }, [loading, sessionKey]);

  // Gendan seneste service fra databasen hvis brugeren valgte at fortsætte fra seneste
  useEffect(() => {
    const resumeFrom = searchParams.get('resume_from');
    if (loading || serverDraftApplied.current || resumeFrom !== 'latest') return;

    const applyServerDraft = async () => {
      try {
        const res = await axios.get(`/api/services/bil/${bilId}/latest`, {
          params: { include_details: 1 }
        });

        if (!res.data?.exists) return;

        const draft = res.data;
        const details = draft.details || {};

        if (draft.kilometerstand !== null && draft.kilometerstand !== undefined) {
          setKilometerstand(String(draft.kilometerstand));
        }

        if (draft.mechanic_signature) {
          setMechanicSignature(draft.mechanic_signature);
        }

        const nextActive = { ...activeSubcategories };
        const nextResults = { ...subcategoryResults };
        const nextNotesActive = { ...notesActive };

        Object.entries(details.subcategoryResults || {}).forEach(([subIdStr, data]) => {
          const subId = parseInt(subIdStr, 10);
          if (!Number.isFinite(subId)) return;
          nextActive[subId] = true;

          const fields = subcategoryFields[subId] || [];
          const rowCount = Math.max(
            data.is_approved?.length || 0,
            data.description?.length || 0,
            data.notes?.length || 0,
            ...Array.from(RECOGNIZED_KEYS).map((key) => (Array.isArray(data[key]) ? data[key].length : 0))
          );

          const resultForSub = {};
          resultForSub.is_approved = Array.from(data.is_approved || []);
          resultForSub.notes = Array.from(data.notes || []);
          resultForSub.description = Array.from(data.description || []);

          Array.from(RECOGNIZED_KEYS).forEach((key) => {
            resultForSub[key] = Array.from(data[key] || []);
          });

          // Forsøg at genskabe feltværdier ud fra "Label: værdi" beskrivelser
          for (let i = 0; i < rowCount; i++) {
            const desc = resultForSub.description?.[i] || '';

            fields.forEach((field) => {
              const existingArr = Array.isArray(resultForSub[field.name])
                ? resultForSub[field.name]
                : [];

              if (existingArr[i] !== undefined && existingArr[i] !== '') return;

              const parsed = extractValueFromDescription(desc, field.label);
              if (parsed !== null) {
                while (existingArr.length <= i) existingArr.push('');
                existingArr[i] = parsed;
                resultForSub[field.name] = existingArr;
              }
            });
          }

          // Sørg for at alle arrays har samme længde for konsistens
          const normalizeKeys = new Set([
            'is_approved',
            'notes',
            'description',
            ...Array.from(RECOGNIZED_KEYS),
            ...fields.map((f) => f.name)
          ]);

          normalizeKeys.forEach((key) => {
            const arr = Array.isArray(resultForSub[key]) ? resultForSub[key] : [];
            while (arr.length < rowCount) {
              arr.push('');
            }
            resultForSub[key] = arr;
          });

          if (resultForSub.notes.some((note) => (note || '').trim() !== '')) {
            nextNotesActive[subId] = true;
          }

          nextResults[subId] = resultForSub;
        });

        setActiveSubcategories(nextActive);
        setSubcategoryResults(nextResults);
        setNotesActive(nextNotesActive);
        serverDraftApplied.current = true;
      } catch (error) {
        console.error('Kunne ikke genskabe seneste service:', error);
      }
    };

    applyServerDraft();
  }, [activeSubcategories, bilId, loading, notesActive, searchParams, subcategoryFields, subcategoryResults]);

  // Gem session når input ændres
  useEffect(() => {
    if (loading) return;
    try {
      const timestamp = Date.now();
      const data = {
        version: 1,
        ts: timestamp,
        kilometerstand,
        mechanicSignature,
        activeSubcategories,
        subcategoryResults,
        notesActive,
        meta: {
          bilId,
          interntNummer,
          categoryIds,
          categoryNames,
          updatedAt: timestamp
        }
      };
      localStorage.setItem(sessionKey, JSON.stringify(data));
    } catch (_) {}
  }, [kilometerstand, mechanicSignature, activeSubcategories, subcategoryResults, notesActive, sessionKey, loading, bilId, interntNummer, categoryIds, categoryNames]);

  const handleSubcategoryChange = (subcategoryId, field, value, index = 0) => {
    setSubcategoryResults(prev => {
      const current = prev[subcategoryId] || {};
      const fieldData = current[field] || [];
      
      // Sikrer at arrayet er langt nok
      while (fieldData.length <= index) {
        fieldData.push('');
      }
      
      fieldData[index] = value;
      
      return {
        ...prev,
        [subcategoryId]: {
          ...current,
          [field]: fieldData
        }
      };
    });
  };

  const addSubcategoryRow = (subcategoryId) => {
    setSubcategoryResults(prev => {
      const current = prev[subcategoryId] || {};
      const next = { ...current };
      // Ensure each field has an array, push empty for new row
      const fields = subcategoryFields[subcategoryId] || [];
      fields.forEach(field => {
        const key = field.name;
        const arr = Array.isArray(next[key]) ? next[key].slice() : [];
        arr.push('');
        next[key] = arr;
      });
      // Also maintain generic arrays for is_approved/notes/description to satisfy backend schema
      const ia = Array.isArray(next.is_approved) ? next.is_approved.slice() : [];
      ia.push('');
      next.is_approved = ia;
      const notes = Array.isArray(next.notes) ? next.notes.slice() : [];
      notes.push('');
      next.notes = notes;
      const desc = Array.isArray(next.description) ? next.description.slice() : [];
      desc.push('');
      next.description = desc;
      // Default signer til valgt mekaniker for subkategori (én gang)
      if (!Array.isArray(next.signed_by) || next.signed_by.length === 0) {
        next.signed_by = [mechanicSignature || ''];
      }
      return { ...prev, [subcategoryId]: next };
    });
  };

  const removeSubcategoryRow = (subcategoryId, index) => {
    setSubcategoryResults(prev => {
      const current = prev[subcategoryId] || {};
      const next = { ...current };
      Object.keys(next).forEach(key => {
        if (Array.isArray(next[key])) {
          const arr = next[key].slice();
          if (index >= 0 && index < arr.length) {
            arr.splice(index, 1);
            next[key] = arr;
          }
        }
      });
      return { ...prev, [subcategoryId]: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!mechanicSignature.trim()) {
      alert('Vælg venligst en mekaniker');
      return;
    }

    setSaving(true);
    
    try {
      // Transform dynamic fields til backend-format
      const payloadResults = {};
      Object.keys(activeSubcategories).forEach(subIdStr => {
        const subId = parseInt(subIdStr);
        if (!activeSubcategories[subId]) return;
        const current = subcategoryResults[subId] || {};
        // find antal rækker via max længde af arrays
        const rowCount = Object.values(current).reduce((max, v) => Array.isArray(v) ? Math.max(max, v.length) : max, 0) || 1;
        const resultForSub = {};
        // init arrays
        resultForSub.is_approved = Array(rowCount).fill('OK');
        resultForSub.notes = Array(rowCount).fill('');
        resultForSub.description = Array(rowCount).fill('');
        // recognized fields arrays
        RECOGNIZED_KEYS.forEach(k => { resultForSub[k] = []; });
        for (let i = 0; i < rowCount; i++) {
          const parts = [];
          const rawDescription = Array.isArray(current.description) ? (current.description[i] ?? '') : '';
          const fields = subcategoryFields[subId] || [];
          fields.forEach(field => {
            const key = field.name;
            let val = Array.isArray(current[key]) ? (current[key][i] ?? '') : '';
            if (typeof val === 'string') {
              const labelPrefix = new RegExp(`^\\s*${field.label}\\s*:\\s*`, 'i');
              val = val.replace(labelPrefix, '').trim();
            }
            if (RECOGNIZED_KEYS.has(key)) {
              // ensure array length
              if (!Array.isArray(resultForSub[key])) resultForSub[key] = [];
              resultForSub[key].push(val);
            } else {
              if (val !== '' && val !== undefined && val !== null) {
                parts.push(`${field.label}: ${val}`);
              }
            }
          });
          // Capture recognized fields that are not part of subcategoryFields (fx. signed_by)
          RECOGNIZED_KEYS.forEach(key => {
            const existing = Array.isArray(resultForSub[key]) ? resultForSub[key] : [];
            const val = Array.isArray(current[key]) ? (current[key][i] ?? '') : '';
            if (existing[i] === undefined) {
              existing[i] = val;
            }
            resultForSub[key] = existing;
          });
          // append combined fields to description to persist alt indhold
          const combined = parts.join('; ');
          resultForSub.description[i] = combined || rawDescription;
        }
        payloadResults[subId] = resultForSub;
      });

      const now = new Date();
      const completedDate = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0')
      ].join('-');

      const saveRes = await axios.post('/api/services/save', {
        subcategory_results: payloadResults,
        bil_id: bilId,
        internt_nummer: interntNummer,
        mechanic_signature: mechanicSignature,
        selected_categories: categoryIds,
        mot_kilometerstand: kilometerstand,
        completed_date: completedDate
      });
      const created = saveRes.data?.result_ids || [];
      // Hent HTML for hver oprettet kategori-resultat og åbn print-dialog
      for (const resultId of created) {
        try {
          const pdfRes = await axios.get(`/api/services/result/${resultId}/pdf`);
          const html = pdfRes.data?.html || '';
          if (html) {
            const win = window.open('', '_blank');
            if (win) {
              win.document.write(html);
              win.document.close();
              win.focus();
              // Brugeren kan gemme som PDF via browserens print-dialog
              win.print();
            }
          }
        } catch (e) {
          console.error('Kunne ikke hente HTML for resultat', resultId, e);
        }
      }

      alert('Service gemt succesfuldt! HTML åbnet.');
      try {
        const timestamp = Date.now();
        const snapshot = {
          version: 1,
          ts: timestamp,
          kilometerstand,
          mechanicSignature,
          activeSubcategories,
          subcategoryResults,
          notesActive,
          meta: {
            bilId,
            interntNummer,
            categoryIds,
            categoryNames,
            updatedAt: timestamp,
            lastSavedAt: timestamp
          }
        };
        localStorage.setItem(sessionKey, JSON.stringify(snapshot));
      } catch (_) {}
      navigate(`/biler/profil/${bilId}`);
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Der opstod en fejl under gem af service');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  // Gruppér kategorier og underkategorier
  const groupedCategories = {};
  categories.forEach(item => {
    if (!groupedCategories[item.category_id]) {
      groupedCategories[item.category_id] = {
        id: item.category_id,
        name: item.category_name,
        subcategories: []
      };
    }
    if (item.subcategory_id) {
      groupedCategories[item.category_id].subcategories.push({
        id: item.subcategory_id,
        name: item.subcategory_name
      });
    }
  });

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => {
            if (bilId) {
              sessionStorage.setItem(`serviceSelection:skipAutoResume:${bilId}`, '1');
            }
            navigate(`/services/selection?bil_id=${bilId}&internt_nummer=${interntNummer}`);
          }}
          className="p-2 text-gray-600 hover:text-gray-800"
        >
          <FaArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Service Detaljer</h1>
          <p className="text-gray-600">Køretøj: <strong>{interntNummer}</strong></p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Kilometerstand og mekaniker */}
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kilometerstand *
              </label>
              <input
                type="number"
                value={kilometerstand}
                onChange={(e) => setKilometerstand(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mekaniker *
              </label>
              <select
                value={mechanicSignature}
                onChange={(e) => setMechanicSignature(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
              >
                <option value="">Vælg mekaniker</option>
                {mekanikere.map((mekaniker, index) => (
                  <option key={index} value={mekaniker}>
                    {mekaniker}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Service kategorier */}
        {Object.values(groupedCategories).map((category) => (
          <div key={category.id} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow">
            {/* Kategori header */}
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">{category.name}</h2>
            </div>

            {/* Underkategorier */}
            <div className="p-6 space-y-6">
              {category.subcategories.map((subcategory) => (
                <div key={subcategory.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">{subcategory.name}</h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const next = !Boolean(activeSubcategories[subcategory.id]);
                          setActiveSubcategories(prev => ({ ...prev, [subcategory.id]: next }));
                          if (next) {
                            addSubcategoryRow(subcategory.id);
                          } else {
                            setSubcategoryResults(prev => ({ ...prev, [subcategory.id]: {} }));
                          }
                        }}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                          activeSubcategories[subcategory.id] ? 'bg-green-600 border-green-700 text-white' : 'bg-white border-gray-300 text-gray-500'
                        }`}
                        title={activeSubcategories[subcategory.id] ? 'Fjern godkendelse' : 'Godkend'}
                      >
                        <FaCheck className="text-sm" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (!activeSubcategories[subcategory.id]) {
                            setActiveSubcategories(prev => ({ ...prev, [subcategory.id]: true }));
                          }
                          const next = !Boolean(notesActive[subcategory.id]);
                          setNotesActive(prev => ({ ...prev, [subcategory.id]: next }));
                          if (!next) {
                            // Clear notes for this subcategory when deactivated
                            setSubcategoryResults(prev => {
                              const current = prev[subcategory.id] || {};
                              const updatedSub = { ...current };
                              // Fjern notes indhold
                              updatedSub.notes = [];
                              return { ...prev, [subcategory.id]: updatedSub };
                            });
                          } else {
                            // Tilføj første række hvis ingen eksisterer
                            const rows = (subcategoryResults[subcategory.id]?.is_approved || []).length;
                            if (!rows) addSubcategoryRow(subcategory.id);
                          }
                        }}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full border transition-colors ${
                          (notesActive[subcategory.id] || ((subcategoryResults[subcategory.id]?.notes || []).some(n => n && String(n).trim() !== '')))
                            ? 'bg-blue-600 border-blue-700 text-white'
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                        title="Tilføj noter-linje"
                      >
                        <FaStickyNote className="text-sm" />
                      </button>
                    </div>
                  </div>

                  {Boolean(activeSubcategories[subcategory.id]) && (
                    <>
                      {(subcategoryResults[subcategory.id]?.is_approved || ['']).map((_, index) => {
                        const fieldsForSub = (subcategoryFields[subcategory.id] || []);
                        const hasFields = fieldsForSub.length > 0;
                        const noteVal = subcategoryResults[subcategory.id]?.notes?.[index] || '';
                        const hasFieldContent = hasFields && fieldsForSub.some(field => {
                          const val = subcategoryResults[subcategory.id]?.[field.name]?.[index];
                          return val && String(val).trim() !== '';
                        });
                        const hasContent = hasFieldContent || (noteVal && String(noteVal).trim() !== '');
                        const rowClasses = `grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 ${hasContent ? 'p-4 bg-gray-50 rounded' : ''}`;
                        return (
                          <div key={index} className={rowClasses}>
                          {fieldsForSub.map(field => {
                            const value = subcategoryResults[subcategory.id]?.[field.name]?.[index] || '';
                            const onChange = (val) => handleSubcategoryChange(subcategory.id, field.name, val, index);
                            if (field.type === 'textarea') {
                              return (
                                <div key={field.name} className="md:col-span-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                  <textarea
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                    rows={3}
                                  />
                                </div>
                              );
                            }
                            if (field.type === 'select') {
                              let options = [];
                              try { options = field.options ? JSON.parse(field.options) : []; } catch (_) { options = []; }
                              return (
                                <div key={field.name}>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                  <select
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                  >
                                    <option value="">-- Vælg --</option>
                                    {options.map((opt, i) => (
                                      <option key={i} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                </div>
                              );
                            }
                            if (field.type === 'date') {
                              return (
                                <div key={field.name}>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                  <input
                                    type="date"
                                    value={value}
                                    onChange={(e) => onChange(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                  />
                                </div>
                              );
                            }
                            if (field.type === 'checkbox') {
                              return (
                                <div key={field.name} className="flex items-center mt-6">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(value)}
                                    onChange={(e) => onChange(e.target.checked ? '1' : '')}
                                    className="mr-2"
                                  />
                                  <label className="text-sm text-gray-700">{field.label}</label>
                                </div>
                              );
                            }
                            // default text
                            return (
                              <div key={field.name}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                <input
                                  type="text"
                                  value={value}
                                  onChange={(e) => onChange(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                />
                              </div>
                            );
                          })}

                          

                          {/* Noter felt pr. række - vis kun når aktiv eller der er indhold */}
                          { (notesActive[subcategory.id] || ((subcategoryResults[subcategory.id]?.notes?.[index] || '').trim() !== '')) && (
                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Noter</label>
                              <textarea
                                value={(subcategoryResults[subcategory.id]?.notes?.[index]) || ''}
                                onChange={(e) => handleSubcategoryChange(subcategory.id, 'notes', e.target.value, index)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                                rows={2}
                                placeholder="Tilføj noter..."
                              />
                            </div>
                          )}
                          {(subcategoryResults[subcategory.id]?.is_approved || ['']).length > 1 && (
                            <div className="md:col-span-2 flex justify-end">
                              <button
                                type="button"
                                onClick={() => removeSubcategoryRow(subcategory.id, index)}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Fjern række
                              </button>
                            </div>
                          )}
                          </div>
                        );
                      })}

                      {/* Add row button */}
                      {subcategoryRepeatable[subcategory.id] && (
                        <button
                          type="button"
                          onClick={() => addSubcategoryRow(subcategory.id)}
                          className="text-falck-blue hover:text-blue-700 text-sm font-medium"
                        >
                          + Tilføj en linje mere
                        </button>
                      )}
                      {/* Underskrift felt nederst pr. underkategori */}
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Underskrift (mekaniker)</label>
                          <select
                            value={(subcategoryResults[subcategory.id]?.signed_by?.[0]) || (mechanicSignature || '')}
                            onChange={(e) => handleSubcategoryChange(subcategory.id, 'signed_by', e.target.value, 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent"
                          >
                            <option value="">Vælg mekaniker</option>
                            {mekanikere.map((m, i) => (
                              <option key={i} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-6">
          <button
            type="button"
            onClick={() => navigate(`/services/selection?bil_id=${bilId}&internt_nummer=${interntNummer}`)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Tilbage
          </button>
          <button
            type="submit"
            disabled={saving}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md flex items-center gap-2 transition-colors ${
              saving 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-falck-blue hover:bg-blue-700'
            }`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Gemmer...
              </>
            ) : (
              <>
                <FaSave />
                Gem Service
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ServiceDetails;
